import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Gem, TrendingDown, TrendingUp } from 'lucide-react'
import { ResponsiveLine } from '@nivo/line'

const COLOR_MULTIPLIER = { red: 2, black: 2, green: 14 }
const SESSION_BREAK_ROUNDS = 5

const toNumber = (value) => {
  const num = Number(value)
  return Number.isFinite(num) ? num : 0
}

const getRoundBets = (round) => {
  if (Array.isArray(round?.bets)) return round.bets
  if (Array.isArray(round?.users)) return round.users
  return []
}

const getFallbackNetFromRound = (round) => {
  const stake = toNumber(
    round?.you_sum ??
    round?.my_sum ??
    round?.sum_you ??
    round?.user_sum
  )

  const payout = toNumber(
    round?.you_win_sum ??
    round?.my_win_sum ??
    round?.sum_you_win ??
    round?.user_win_sum ??
    round?.you_payout
  )

  if (stake <= 0 && payout <= 0) return null
  return payout - stake
}

const getRoundNet = (round, { userChatId = null } = {}) => {
  const bets = getRoundBets(round)
  const betsWithMarker = bets.filter((bet) => Boolean(bet?.your_bet || bet?.you))
  const betsByChatId = userChatId === null
    ? []
    : bets.filter((bet) => String(bet?.chat_id || '') === String(userChatId))
  const myBets = betsWithMarker.length > 0
    ? betsWithMarker
    : betsByChatId

  if (myBets.length > 0) {
    const winColor = String(round?.win_color || '').toLowerCase()
    const multiplier = COLOR_MULTIPLIER[winColor] || 0

    const totalStake = myBets.reduce((acc, bet) => acc + toNumber(bet?.sum), 0)
    const winningStake = myBets.reduce((acc, bet) => {
      return String(bet?.color || '').toLowerCase() === winColor
        ? acc + toNumber(bet?.sum)
        : acc
    }, 0)

    const payout = winningStake * multiplier
    return payout - totalStake
  }

  if (round?.you) {
    return getFallbackNetFromRound(round)
  }

  return null
}

const buildSeries = ({ allRounds, myRounds }) => {
  const normalizedAll = Array.isArray(allRounds) ? allRounds : []
  const normalizedMy = Array.isArray(myRounds) ? myRounds : []
  const scopedRounds = normalizedAll
  const myRoundIds = new Set(normalizedMy.map((round) => String(round?.id || '')))
  const myRoundsById = new Map(normalizedMy.map((round) => [String(round?.id || ''), round]))
  const userChatId = normalizedMy
    .flatMap((round) => getRoundBets(round))
    .map((bet) => String(bet?.chat_id || ''))
    .find(Boolean) || null

  const myRoundsWithIndex = scopedRounds
    .map((round, index) => {
      const roundId = String(round?.id || '')
      const myRound = myRoundsById.get(roundId)
      const sourceRound = myRound || round
      const roundNet = getRoundNet(sourceRound, { userChatId })
      const isMyRound = myRoundIds.has(roundId) || sourceRound?.you === true || roundNet !== null

      if (!isMyRound || roundNet === null) return null

      return {
        index,
        id: roundId,
        net: roundNet,
        win: roundNet > 0
      }
    })
    .filter(Boolean)

  const latestMyRound = myRoundsWithIndex[0]
  if (latestMyRound?.index >= SESSION_BREAK_ROUNDS) {
    return {
      series: [{ x: 0, y: 0, id: 'start', net: 0, win: null }],
      roundsUsed: 0,
      totalNet: 0,
      wins: 0,
      losses: 0
    }
  }

  const currentSessionRounds = []

  for (let i = 0; i < myRoundsWithIndex.length; i += 1) {
    const currentRound = myRoundsWithIndex[i]
    const previousRound = myRoundsWithIndex[i - 1]

    if (i > 0 && previousRound) {
      const roundsBetween = currentRound.index - previousRound.index - 1

      if (roundsBetween >= SESSION_BREAK_ROUNDS) {
        break
      }
    }

    currentSessionRounds.push(currentRound)
  }

  const myRecentRounds = currentSessionRounds.reverse()

  let cumulative = 0
  const series = [{ x: 0, y: 0, id: 'start', net: 0, win: null }]

  myRecentRounds.forEach((round, index) => {
    cumulative += round.net
    series.push({
      x: index + 1,
      y: cumulative,
      id: round.id,
      net: round.net,
      win: round.win
    })
  })

  const totalNet = cumulative
  const wins = myRecentRounds.filter((round) => round.net > 0).length
  const losses = myRecentRounds.filter((round) => round.net < 0).length

  return {
    series,
    roundsUsed: myRecentRounds.length,
    totalNet,
    wins,
    losses
  }
}

const DoublePerformanceChart = ({ allRounds = [], myRounds = [] }) => {
  const { t } = useTranslation()

  const performance = useMemo(() => {
    return buildSeries({ allRounds, myRounds })
  }, [allRounds, myRounds])

  const chartData = useMemo(() => {
    return [
      {
        id: 'balance',
        data: performance.series.map((point) => ({
          x: point.x,
          y: point.y,
          net: point.net,
          roundId: point.id
        }))
      }
    ]
  }, [performance.series])

  const totalNetSign = performance.totalNet > 0 ? '+' : performance.totalNet < 0 ? '-' : ''
  const totalNetAbs = Math.abs(performance.totalNet)
  const lineColor = performance.totalNet >= 0 ? '#34d399' : '#fb7185'
  const lineAreaColor = performance.totalNet >= 0 ? 'rgba(52,211,153,0.18)' : 'rgba(251,113,133,0.18)'

  return (
    <div className="rounded-2xl border border-white/10 bg-surface/50 p-4">
      <div>
        <div>
          <h3 className="text-sm font-semibold text-white">
            {t('double.performance.title', 'Баланс моей игры')}
          </h3>
        </div>
      </div>

      {performance.roundsUsed > 0 ? (
        <>
          <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
            <div className="rounded-lg border border-white/10 bg-dark-primary/40 p-2">
              <p className="text-text-secondary">{t('double.performance.total', 'Итог')}</p>
              <p className={`mt-1 inline-flex items-center gap-1 font-semibold ${
                performance.totalNet > 0
                  ? 'text-emerald-300'
                  : performance.totalNet < 0
                    ? 'text-rose-300'
                    : 'text-white'
              }`}>
                <Gem className="h-3.5 w-3.5" />
                <span>{totalNetSign}{totalNetAbs.toLocaleString()}</span>
              </p>
            </div>

            <div className="rounded-lg border border-white/10 bg-dark-primary/40 p-2">
              <p className="text-text-secondary">{t('double.performance.wins', 'Победы')}</p>
              <p className="mt-1 inline-flex items-center gap-1 font-semibold text-emerald-300">
                <TrendingUp className="h-3.5 w-3.5" />
                <span>{performance.wins}</span>
              </p>
            </div>

            <div className="rounded-lg border border-white/10 bg-dark-primary/40 p-2">
              <p className="text-text-secondary">{t('double.performance.losses', 'Поражения')}</p>
              <p className="mt-1 inline-flex items-center gap-1 font-semibold text-rose-300">
                <TrendingDown className="h-3.5 w-3.5" />
                <span>{performance.losses}</span>
              </p>
            </div>
          </div>

          <div className="mt-3 h-28 w-full rounded-xl border border-white/10 bg-dark-primary/40 p-2">
            <div className="h-full w-full" role="img" aria-label={t('double.performance.aria', 'График баланса игры в Double')}>
              <ResponsiveLine
                data={chartData}
                margin={{ top: 8, right: 8, bottom: 8, left: 8 }}
                xScale={{ type: 'point' }}
                yScale={{
                  type: 'linear',
                  min: 'auto',
                  max: 'auto',
                  stacked: false,
                  reverse: false
                }}
                axisTop={null}
                axisRight={null}
                axisBottom={null}
                axisLeft={null}
                enableGridX={false}
                enableGridY={false}
                curve="monotoneX"
                colors={[lineColor]}
                lineWidth={3}
                enablePoints={false}
                useMesh
                animate={false}
                isInteractive
                enableArea
                areaOpacity={1}
                areaBaselineValue={0}
                defs={[
                  {
                    id: 'doublePerformanceArea',
                    type: 'linearGradient',
                    colors: [
                      { offset: 0, color: lineAreaColor },
                      { offset: 100, color: 'rgba(0,0,0,0)' }
                    ]
                  }
                ]}
                fill={[{ match: '*', id: 'doublePerformanceArea' }]}
                markers={[
                  {
                    axis: 'y',
                    value: 0,
                    lineStyle: {
                      stroke: 'rgba(148,163,184,0.45)',
                      strokeWidth: 1,
                      strokeDasharray: '4 4'
                    }
                  }
                ]}
                tooltip={({ point }) => {
                  const rawY = Number(point?.data?.y ?? 0)
                  const rawNet = Number(point?.data?.net ?? 0)
                  const ySign = rawY > 0 ? '+' : rawY < 0 ? '-' : ''
                  const netSign = rawNet > 0 ? '+' : rawNet < 0 ? '-' : ''

                  return (
                    <div className="rounded-lg border border-white/15 bg-dark-primary/95 px-2 py-1.5 text-xs text-white shadow-xl">
                      <div className="text-text-secondary">
                        {t('double.performance.point', 'Игра')} #{point?.data?.x}
                      </div>
                      <div className="mt-0.5 font-semibold">
                        {t('double.performance.total', 'Итог')}: {ySign}{Math.abs(rawY).toLocaleString()} <Gem className="inline h-3 w-3" />
                      </div>
                      <div className="text-text-secondary">
                        {t('double.performance.delta', 'Изменение')}: {netSign}{Math.abs(rawNet).toLocaleString()} <Gem className="inline h-3 w-3" />
                      </div>
                    </div>
                  )
                }}
                theme={{
                  tooltip: {
                    container: {
                      background: 'transparent',
                      boxShadow: 'none',
                      padding: 0
                    }
                  },
                  crosshair: {
                    line: {
                      stroke: 'rgba(148,163,184,0.45)',
                      strokeWidth: 1,
                      strokeDasharray: '3 3'
                    }
                  }
                }}
              />
            </div>
          </div>
        </>
      ) : (
        <div className="mt-3 rounded-xl border border-white/10 bg-dark-primary/40 p-3 text-xs text-text-secondary">
          {t('double.performance.empty', 'Сделайте первую ставку, чтобы увидеть график баланса вашей игры')}
        </div>
      )}
    </div>
  )
}

export default DoublePerformanceChart
